import { GenericModelController } from "@/controllers/model-controllers/abstract/generic-model-controller.ts"
import { IncomingInvoiceRentalIntakeMaterialization } from "@/models/incoming-invoice-rental-intake-materialization.model.ts"
import type { IIncomingInvoiceRentalIntakeMaterialization } from "@/interfaces/api/incoming-invoice-rental-intake-materialization.interface.ts"
import { IncomingInvoiceRentalIntakeMaterializationRepository } from "@/repositories/incoming-invoice-rental-intake-materialization-repository.ts"
import { useSocketSynchronizationHandlers } from "@/composables/socket/socket-synchronization-handlers.ts"
import { useIncomingInvoiceStore } from "@/store/incoming-invoices.store.ts"
import { storeToRefs } from "pinia"
import { useRelatedObjectsManager } from "@/composables/socket/related-objects-manager.ts"
import type { IncomingInvoice } from "@/models/incoming-invoice.model.ts"
import { useRentalIntakeMaterializationStore } from "@/store/rental-intake-materializations.store.ts"

export class IncomingInvoiceRentalIntakeMaterializationController extends GenericModelController<
  IncomingInvoiceRentalIntakeMaterialization,
  IIncomingInvoiceRentalIntakeMaterialization,
  IncomingInvoiceRentalIntakeMaterializationRepository
> {
  constructor() {
    const incomingInvoicesStore = useIncomingInvoiceStore()
    const rentalIntakeMaterializationStore = useRentalIntakeMaterializationStore()
    const { incomingInvoices } = storeToRefs(incomingInvoicesStore)
    const { rentalIntakeMaterializations } = storeToRefs(rentalIntakeMaterializationStore)

    const {
      pushToRelatedObjects: pushToRelatedIncomingInvoice,
      removeFromRelatedObjects: removeFromRelatedIncomingInvoice
    } = useRelatedObjectsManager(
      incomingInvoices,
      (incomingInvoiceRentalIntakeMaterialization: IncomingInvoiceRentalIntakeMaterialization) =>
        incomingInvoiceRentalIntakeMaterialization.incomingInvoice,
      (incomingVoice: IncomingInvoice) => incomingVoice.id,
      (incomingInvoice: IncomingInvoice) =>
        incomingInvoice.incomingInvoiceRentalIntakeMaterializations
    )

    const {
      pushToRelatedObjects: pushToRelatedRentalIntakeMaterialization,
      removeFromRelatedObjects: removeFromRelatedRentalIntakeMaterialization
    } = useRelatedObjectsManager(
      rentalIntakeMaterializations,
      (incomingInvoiceRentalIntakeMaterialization: IncomingInvoiceRentalIntakeMaterialization) =>
        incomingInvoiceRentalIntakeMaterialization.rentalIntakeMaterialization,
      (rentalIntakeMaterialization) => rentalIntakeMaterialization.id,
      (rentalIntakeMaterialization) =>
        rentalIntakeMaterialization.incomingInvoiceRentalIntakeMaterializations
    )

    function pushToRelatedObjects(model: IncomingInvoiceRentalIntakeMaterialization) {
      pushToRelatedIncomingInvoice(model)
      pushToRelatedRentalIntakeMaterialization(model)
    }

    function removeFromRelatedObjects(model: IncomingInvoiceRentalIntakeMaterialization) {
      removeFromRelatedIncomingInvoice(model)
      removeFromRelatedRentalIntakeMaterialization(model)
    }

    const storeFunctionsConfig = {
      onCreate: pushToRelatedObjects,
      onDelete: removeFromRelatedObjects
    }

    super(
      IncomingInvoiceRentalIntakeMaterialization,
      storeFunctionsConfig,
      new IncomingInvoiceRentalIntakeMaterializationRepository()
    )

    this.socketSynchronizationHandlers = useSocketSynchronizationHandlers(
      IncomingInvoiceRentalIntakeMaterialization,
      null,
      pushToRelatedObjects,
      removeFromRelatedObjects
    )
  }
}
